import { SocialPlatform } from '@/lib/api';

export type ComposerPlatform = SocialPlatform | 'tiktok';

export const PLATFORM_CHARACTER_LIMITS: Record<ComposerPlatform, number> = {
  twitter: 280,
  linkedin: 3000,
  facebook: 63206,
  instagram: 2200,
  tiktok: 2200,
  youtube: 5000,
  telegram: 4096,
};

export const ALL_PLATFORMS: ComposerPlatform[] = [
  'twitter',
  'linkedin',
  'facebook',
  'instagram',
  'tiktok',
];

/**
 * Platforms currently offered to users for connecting accounts and publishing.
 *
 * Facebook and Instagram are intentionally hidden until Meta API credentials
 * are configured and the OAuth + publish flows are verified end-to-end. This is
 * a temporary, fully-reversible hide: the `SocialPlatform` type, the FB/IG
 * entity configs/clients, and every per-platform rendering map deliberately
 * still include facebook/instagram. To re-enable, add them back here AND remove
 * the matching guard in the backend OAuth init route (keep the
 * `LAUNCHED_PLATFORMS` set in
 * services/api-service/src/routes/arches/social-media/accounts.ts in sync).
 */
export const LAUNCHED_PLATFORMS: SocialPlatform[] = ['twitter', 'linkedin'];

/**
 * Per-platform limits on attached media
 */
export interface PlatformMediaLimit {
  /** Max total files regardless of type */
  maxFiles: number;
  /** Max images (JPEGs, PNGs, WEBPs) */
  maxImages: number;
  /** Max videos */
  maxVideos: number;
  /** Max GIFs (animated) */
  maxGifs: number;
  /** Accepted MIME types */
  acceptedMimeTypes: readonly string[];
}

export const PLATFORM_MEDIA_LIMITS: Record<ComposerPlatform, PlatformMediaLimit> = {
  twitter: {
    maxFiles: 4,
    maxImages: 4,
    maxVideos: 1,
    maxGifs: 1,
    acceptedMimeTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'video/mp4',
      'video/quicktime',
    ],
  },
  instagram: {
    maxFiles: 10,
    maxImages: 10,
    maxVideos: 10,
    maxGifs: 0,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'video/mp4', 'video/quicktime'],
  },
  linkedin: {
    maxFiles: 9,
    maxImages: 9,
    maxVideos: 1,
    maxGifs: 0,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
  },
  facebook: {
    maxFiles: 10,
    maxImages: 10,
    maxVideos: 1,
    maxGifs: 0,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/quicktime'],
  },
  tiktok: {
    maxFiles: 1,
    maxImages: 0,
    maxVideos: 1,
    maxGifs: 0,
    acceptedMimeTypes: ['video/mp4', 'video/quicktime'],
  },
  youtube: {
    maxFiles: 1,
    maxImages: 0,
    maxVideos: 1,
    maxGifs: 0,
    acceptedMimeTypes: ['video/mp4', 'video/quicktime'],
  },
  telegram: {
    maxFiles: 10,
    maxImages: 10,
    maxVideos: 1,
    maxGifs: 1,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
  },
};

/**
 * Returns the effective media limit when posting to multiple platforms
 * (uses the most restrictive maxFiles across all platforms).
 */
export function getEffectiveMediaLimit(platforms: ComposerPlatform[]): PlatformMediaLimit {
  if (platforms.length === 0) return PLATFORM_MEDIA_LIMITS.twitter; // most restrictive default
  return platforms.reduce<PlatformMediaLimit>((strictest, platform) => {
    const limit = PLATFORM_MEDIA_LIMITS[platform];
    return limit.maxFiles < strictest.maxFiles ? limit : strictest;
  }, PLATFORM_MEDIA_LIMITS[platforms[0]]);
}
